<script setup lang="ts">
// Spec 2026-10-04 §3.4: Japan live synthetic NAV tile. Tier colours follow the agent-pill rule:
// green live, amber degraded/stale, grey only when the fetch itself failed, never red.
import { computed } from 'vue'
import type { JapanSynthNavLive } from '../../api/client'

// fetchFailed: the LATEST poll failed (follow-up item 5) — grey even if older data is still held.
const props = defineProps<{ data: JapanSynthNavLive | null; fetchFailed?: boolean }>()

const REASONS: Record<string, string> = {
  contract_roll: 'NIY contract roll — waiting for the next official NAV',
  contract_unknown: 'NIY contract since the last NAV unknown — waiting for the next official NAV',
  no_fx: 'GBPJPY feed missing',
  no_equity: 'NIY feed missing',
  no_anchor: 'No official NAV yet',
  no_anchor_tokyo_close: 'No Tokyo close for the last NAV',
  no_anchor_fx: 'No noon GBPJPY fix for the last NAV',
  no_tokyo_close_today: "Today's Tokyo close not captured",
  guard: 'Implausible move — value withheld',
}
const unavailable = computed(() => props.data == null || props.fetchFailed === true)
const tierClass = computed(() =>
  unavailable.value ? 'tier-grey' : props.data!.tier === 'live' ? 'tier-live' : 'tier-amber')
const tierLabel = computed(() =>
  unavailable.value || props.data == null ? 'unavailable'
    : props.data.tier === 'live' ? 'live'
      : props.data.tier === 'degraded' ? 'delayed' : 'stale')
const phaseLabel = computed(() =>
  props.data?.phase === 'pre_strike' ? 'Strike pending — Tokyo close locked' : 'Tracking NIY since the last strike')
const nav = computed(() => props.data?.synthNavPence != null ? `${props.data.synthNavPence.toFixed(2)}p` : null)
const move = computed(() => {
  const r = props.data?.returnFrac
  return r == null ? null : `${r >= 0 ? '+' : ''}${(r * 100).toFixed(2)}%`
})
// Input ages measured from the publish instant (bars are stamped at their START; the close is +1 min).
function age(tsUtc: string | null | undefined): string | null {
  if (!tsUtc || !props.data) return null
  const s = Math.max(0, Math.round((Date.parse(props.data.computedAtUtc) - (Date.parse(tsUtc) + 60_000)) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}
const ages = computed(() => {
  if (!props.data) return null
  const niy = props.data.phase === 'pre_strike' ? 'Tokyo close locked' : age(props.data.equity.tsUtc)
  const fx = age(props.data.fx.tsUtc)
  return [niy && `${props.data.equity.source} ${niy}`, fx && `FX ${fx}`].filter(Boolean).join(' · ') || null
})
const reason = computed(() => props.data?.reason ? (REASONS[props.data.reason] ?? props.data.reason) : null)
</script>

<template>
  <section class="jl-card" data-testid="japan-live-synth-card">
    <header class="jl-head">
      <span class="jl-title">Live synthetic NAV</span>
      <span class="jl-pill" :class="tierClass" data-testid="japan-live-synth-tier">{{ tierLabel }}</span>
    </header>
    <div v-if="nav" class="jl-body">
      <span class="jl-nav" data-testid="japan-live-synth-nav">{{ nav }}</span>
      <span v-if="move" class="jl-move" data-testid="japan-live-synth-move">{{ move }} vs {{ data?.anchorDate }} official</span>
    </div>
    <div v-else-if="reason" class="jl-reason" data-testid="japan-live-synth-reason">{{ reason }}</div>
    <div v-if="data" class="jl-foot">{{ phaseLabel }} · {{ data.equity.source }}</div>
    <div v-if="ages" class="jl-foot" data-testid="japan-live-synth-ages">{{ ages }}</div>
  </section>
</template>

<style scoped>
.jl-card { border: 1px solid var(--border, #d0d7de); border-radius: 8px; padding: 12px; margin: 12px 0; }
.jl-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.jl-title { font-weight: 600; }
.jl-pill { font-size: 12px; padding: 2px 8px; border-radius: 999px; }
.tier-live { background: #dafbe1; color: #116329; }
.tier-amber { background: #fff8c5; color: #7d4e00; }
.tier-grey { background: #eaeef2; color: #57606a; }
.jl-body { display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px; margin-top: 8px; }
.jl-nav { font-size: 22px; font-weight: 600; }
.jl-move, .jl-foot, .jl-reason { font-size: 13px; color: var(--muted, #57606a); }
.jl-foot { margin-top: 6px; }
</style>
